/**
 * FpcAlternativesPanel
 *
 * A popover panel that shows FPC alternative foods for a given intake row.
 * Alternatives are displayed in descending specificity order (level 4 → 3 → 2 → 1).
 *
 * Features:
 * - Search input triggers server-side search across the entire food table.
 * - When no search term: shows FPC-grouped results with infinite scroll per level.
 * - Pantry availability indicators: green (enough), yellow (partial), gray (missing).
 * - Adding an alternative reserves it from pantry immediately.
 * - Activating/deactivating an alternative swaps pantry reservations.
 */

import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { Shuffle, Search, Sparkles, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { useFpcLabels, getFpcSectionLabel, getFpcLevel } from "@/lib/fpc";
import type { Prijem } from "../types";
import { useDebounce } from "@/hooks/useDebounce";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface FpcAlternativeFood {
  id: number;
  nazov: string;
  energiaKcal: string | null;
  energiaKj: string | null;
  proteinG: string | null;
  tukG: string | null;
  sacharidG: string | null;
  balenieG: string | null;
  fpcKod: string | null;
  requiredG?: number;
  availableG?: number;
  pantryStatus?: "green" | "yellow" | "red";
  score?: number;
  scoreBreakdown?: {
    fpc: number;
    pantry: number;
    nutrition: number;
    confidence: number;
  };
  reasons?: string[];
}

interface LevelState {
  items: FpcAlternativeFood[];
  offset: number;
  hasMore: boolean;
  loading: boolean;
}

const PAGE_SIZE = 15;

// ---------------------------------------------------------------------------
// Helper: format a numeric nutrient field
// ---------------------------------------------------------------------------
function fmt(v: string | null, decimals = 1): string {
  if (v === null || v === undefined || v === "") return "—";
  const n = parseFloat(v);
  return isNaN(n) ? "—" : n.toFixed(decimals);
}

function kcalPer100(food: FpcAlternativeFood): string {
  if (food.energiaKcal) return `${parseFloat(food.energiaKcal).toFixed(0)}`;
  if (food.energiaKj)
    return `${(parseFloat(food.energiaKj) / 4.18).toFixed(0)}`;
  return "—";
}

function fmtGrams(v: number | undefined): string {
  if (v === undefined || !Number.isFinite(v)) return "—";
  return v >= 1000 ? `${(v / 1000).toFixed(2)} kg` : `${v.toFixed(1)} g`;
}

function toPantryStatus(
  status: FpcAlternativeFood["pantryStatus"],
  fallback: PantryStatus
): PantryStatus {
  if (status === "green") return "full";
  if (status === "yellow") return "partial";
  if (status === "red") return "missing";
  return fallback;
}

// ---------------------------------------------------------------------------
// Pantry badge
// ---------------------------------------------------------------------------
type PantryStatus = "full" | "partial" | "missing";

function PantryBadge({ status }: { status: PantryStatus }) {
  if (status === "full") {
    return (
      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 border border-green-300">
        ✓ špajza
      </span>
    );
  }
  if (status === "partial") {
    return (
      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-yellow-100 text-yellow-700 border border-yellow-300">
        ~ špajza
      </span>
    );
  }
  return null;
}

// ---------------------------------------------------------------------------
// FoodRow: a single alternative food item
// ---------------------------------------------------------------------------

interface FoodRowProps {
  food: FpcAlternativeFood;
  originalPrijemId: number;
  pantryStatus: PantryStatus;
  onAdded: () => void;
}

function FoodRow({
  food,
  originalPrijemId,
  pantryStatus,
  onAdded,
}: FoodRowProps) {
  const utils = trpc.useUtils();
  const addMutation = trpc.prijem.addAlternative.useMutation({
    onSuccess: () => {
      toast.success("Alternatíva pridaná");
      utils.prijem.getByKlientAndDate.invalidate();
      utils.prijem.getDailyTotals.invalidate();
      utils.spajza.getAvailability.invalidate();
      onAdded();
    },
    onError: err => {
      toast.error(`Chyba: ${err.message}`);
    },
  });

  const rowBg =
    pantryStatus === "full"
      ? "bg-green-50 hover:bg-green-100"
      : pantryStatus === "partial"
        ? "bg-yellow-50 hover:bg-yellow-100"
        : "hover:bg-muted/50";

  return (
    <div
      className={`flex items-center justify-between gap-2 rounded px-2 py-1.5 text-sm ${rowBg}`}
    >
      <div className="flex-1 min-w-0">
        <div className="font-medium truncate flex items-center gap-1.5">
          {food.nazov}
          <PantryBadge status={pantryStatus} />
        </div>
        <div className="text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
          <span>
            <span className="text-foreground/60">E:</span> {kcalPer100(food)}{" "}
            kcal
          </span>
          <span>
            <span className="text-foreground/60">P:</span> {fmt(food.proteinG)}{" "}
            g
          </span>
          <span>
            <span className="text-foreground/60">S:</span> {fmt(food.sacharidG)}{" "}
            g
          </span>
          <span>
            <span className="text-foreground/60">T:</span> {fmt(food.tukG)} g
          </span>
          {food.requiredG !== undefined && (
            <span>
              <span className="text-foreground/60">náhrada:</span>{" "}
              {fmtGrams(food.requiredG)}
            </span>
          )}
          {food.score !== undefined && (
            <span>
              <span className="text-foreground/60">skóre:</span>{" "}
              {Math.round(food.score * 100)}%
            </span>
          )}
          {food.fpcKod && (
            <span className="font-mono bg-muted px-1 rounded">
              {food.fpcKod}
            </span>
          )}
        </div>
        {food.reasons && food.reasons.length > 0 && (
          <div className="text-[11px] text-muted-foreground mt-0.5 truncate">
            {food.reasons.slice(0, 3).join(" · ")}
          </div>
        )}
      </div>
      <Button
        size="sm"
        variant="outline"
        className="shrink-0 h-7 text-xs"
        disabled={addMutation.isPending}
        onClick={() =>
          addMutation.mutate({
            originalPrijemId,
            alternativePotravinaId: food.id,
          })
        }
      >
        Pridať
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SmartRecommendations: server-ranked alternatives for the current intake row
// ---------------------------------------------------------------------------

interface SmartRecommendationsProps {
  originalPrijemId: number;
  klientId?: number | null;
  onAdded: () => void;
}

function SmartRecommendations({
  originalPrijemId,
  klientId,
  onAdded,
}: SmartRecommendationsProps) {
  const { data, isLoading } = trpc.potravina.smartAlternativesSwap.useQuery(
    {
      originalPrijemId,
      klientId: klientId!,
      limit: 10,
    },
    { enabled: !!klientId }
  );

  if (!klientId) return null;
  if (isLoading) {
    return (
      <div className="mt-3 rounded-md border border-blue-100 bg-blue-50/50 px-3 py-2 text-xs text-blue-700">
        Hľadám najlepšie výmeny…
      </div>
    );
  }
  if (!data || data.length === 0) return null;

  return (
    <div className="mt-3 rounded-md border border-blue-100 bg-blue-50/40 p-2">
      <div className="flex items-center gap-1.5 text-xs font-semibold text-blue-800 uppercase tracking-wide px-1 mb-2">
        <Sparkles className="h-3.5 w-3.5" />
        Smart odporúčania
      </div>
      <div className="space-y-1">
        {data.map(food => (
          <FoodRow
            key={food.id}
            food={food}
            originalPrijemId={originalPrijemId}
            pantryStatus={toPantryStatus(food.pantryStatus, "missing")}
            onAdded={onAdded}
          />
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inner component: a single level section (lazy-loaded, infinite scroll)
// Used only when there is NO active search query.
// ---------------------------------------------------------------------------

interface LevelSectionProps {
  fpcKod: string;
  level: 1 | 2 | 3 | 4;
  originalPrijemId: number;
  originalPotravinaId: number | null;
  pantryAvailability: Record<number, number>;
  /** Required grams for the original item (to determine full/partial) */
  requiredG: number;
  fpcLabels: Record<string, string>;
  onAdded: () => void;
}

function LevelSection({
  fpcKod,
  level,
  originalPrijemId,
  originalPotravinaId,
  pantryAvailability,
  requiredG,
  fpcLabels,
  onAdded,
}: LevelSectionProps) {
  const utils = trpc.useUtils();
  const [state, setState] = useState<LevelState>({
    items: [],
    offset: 0,
    hasMore: true,
    loading: false,
  });

  // Fetch next page from the server
  const fetchPage = useCallback(async () => {
    if (state.loading || !state.hasMore) return;
    setState(s => ({ ...s, loading: true }));

    try {
      if (!originalPotravinaId) {
        setState(s => ({ ...s, loading: false, hasMore: false }));
        return;
      }
      const results = await utils.potravina.fpcAlternatives.fetch({
        potravinaId: originalPotravinaId,
        level,
        limit: PAGE_SIZE,
        offset: state.offset,
      });

      setState(s => ({
        items: [...s.items, ...results],
        offset: s.offset + results.length,
        hasMore: results.length === PAGE_SIZE,
        loading: false,
      }));
    } catch {
      setState(s => ({ ...s, loading: false }));
    }
  }, [
    state.loading,
    state.hasMore,
    state.offset,
    utils,
    originalPotravinaId,
    level,
  ]);

  // Load first page on mount
  useEffect(() => {
    fetchPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Intersection observer for infinite scroll
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) fetchPage();
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [fetchPage]);

  // Hide section if nothing to show and loading is done
  if (state.items.length === 0 && !state.loading && !state.hasMore) return null;
  if (state.items.length === 0 && !state.loading) return null;

  return (
    <div className="mt-3">
      <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide px-1 mb-1">
        {getFpcSectionLabel(fpcLabels, fpcKod, level)}
      </div>
      <div className="space-y-1">
        {state.items.map(food => {
          const avail = pantryAvailability[food.id] ?? 0;
          const status: PantryStatus =
            avail >= requiredG ? "full" : avail > 0 ? "partial" : "missing";
          return (
            <FoodRow
              key={food.id}
              food={food}
              originalPrijemId={originalPrijemId}
              pantryStatus={status}
              onAdded={onAdded}
            />
          );
        })}
      </div>
      {/* Sentinel for infinite scroll */}
      {state.hasMore && (
        <div ref={sentinelRef} className="h-4 flex items-center justify-center">
          {state.loading && (
            <span className="text-xs text-muted-foreground">Načítavam…</span>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SearchResults: shown when user has typed a search query
// ---------------------------------------------------------------------------

interface SearchResultsProps {
  potravinaId: number;
  fpcKod: string;
  originalPrijemId: number;
  searchQuery: string;
  pantryAvailability: Record<number, number>;
  requiredG: number;
  onAdded: () => void;
}

function SearchResults({
  potravinaId,
  fpcKod,
  originalPrijemId,
  searchQuery,
  pantryAvailability,
  requiredG,
  onAdded,
}: SearchResultsProps) {
  // Use level=4 with search — the server ignores level when search is provided
  const { data, isLoading } = trpc.potravina.fpcAlternatives.useQuery(
    {
      potravinaId,
      level: 4,
      limit: 50,
      offset: 0,
      search: searchQuery,
    },
    { enabled: searchQuery.trim().length >= 2 }
  );

  if (searchQuery.trim().length < 2) {
    return (
      <div className="px-2 py-4 text-sm text-muted-foreground text-center">
        Zadajte aspoň 2 znaky…
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="px-2 py-4 text-sm text-muted-foreground text-center">
        Hľadám…
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="px-2 py-4 text-sm text-muted-foreground text-center">
        Žiadne výsledky
      </div>
    );
  }

  return (
    <div className="space-y-1 mt-2">
      {data.map(food => {
        const avail = pantryAvailability[food.id] ?? 0;
        const status: PantryStatus =
          avail >= requiredG ? "full" : avail > 0 ? "partial" : "missing";
        return (
          <FoodRow
            key={food.id}
            food={food}
            originalPrijemId={originalPrijemId}
            pantryStatus={status}
            onAdded={onAdded}
          />
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface FpcAlternativesPanelProps {
  item: Prijem;
  klientId?: number | null;
}

export function FpcAlternativesPanel({
  item,
  klientId,
}: FpcAlternativesPanelProps) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebounce(searchQuery, 350);
  const fpcKod = item.potravina.fpcKod;

  // Fetch pantry availability for this client
  const { data: availabilityData } = trpc.spajza.getAvailability.useQuery(
    { klientId: klientId! },
    { enabled: open && !!klientId }
  );

  // Build a map: potravinaId → available grams
  const pantryAvailability = useMemo<Record<number, number>>(() => {
    if (!availabilityData) return {};
    const map: Record<number, number> = {};
    for (const entry of availabilityData) {
      map[entry.potravinaId] = entry.dostupneG;
    }
    return map;
  }, [availabilityData]);

  // Required grams for the original item
  const requiredG = item.hmotnost ? parseFloat(item.hmotnost) : 100;

  const { fpcLabels } = useFpcLabels();

  // Don't render the button if there's no FPC code on the food
  if (!fpcKod) return null;

  // Determine which levels to show based on the code's own level
  const foodLevel = getFpcLevel(fpcKod);
  const levelsToShow = ([4, 3, 2, 1] as const).filter(l => l <= foodLevel);

  const handleClose = () => {
    setOpen(false);
    setSearchQuery("");
  };

  const isSearching = debouncedSearch.trim().length >= 2;

  return (
    <Popover
      open={open}
      onOpenChange={v => {
        if (!v) handleClose();
        else setOpen(true);
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          title="Zobraziť FPC alternatívy"
          className="h-8 w-8 p-0"
        >
          <Shuffle className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[420px] max-h-[520px] flex flex-col p-0"
        align="end"
        side="left"
      >
        {/* Header */}
        <div className="px-3 pt-3 pb-2 border-b shrink-0">
          <div className="font-semibold text-sm mb-0.5">
            FPC alternatívy:{" "}
            <span className="font-normal">{item.potravina.nazov}</span>
          </div>
          <div className="text-xs text-muted-foreground mb-2">
            Kód:{" "}
            <span className="font-mono bg-muted px-1 rounded">{fpcKod}</span>
            {" — "}Pridá sa s rovnakou energetickou hodnotou (kcal).
          </div>
          {/* Search input */}
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
            <Input
              className="pl-7 pr-7 h-8 text-sm"
              placeholder="Hľadať potravinu v celej databáze…"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              autoFocus={false}
            />
            {searchQuery && (
              <button
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                onClick={() => setSearchQuery("")}
                tabIndex={-1}
                type="button"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* Scrollable list */}
        <div className="overflow-y-auto flex-1 px-3 pb-3">
          {isSearching ? (
            <SearchResults
              potravinaId={item.potravinaId!}
              fpcKod={fpcKod}
              originalPrijemId={item.id}
              searchQuery={debouncedSearch}
              pantryAvailability={pantryAvailability}
              requiredG={requiredG}
              onAdded={handleClose}
            />
          ) : (
            <div>
              <SmartRecommendations
                originalPrijemId={item.id}
                klientId={klientId}
                onAdded={handleClose}
              />
              <div className="divide-y divide-border">
                {levelsToShow.map(level => (
                  <LevelSection
                    key={level}
                    fpcKod={fpcKod}
                    level={level}
                    originalPrijemId={item.id}
                    originalPotravinaId={item.potravinaId}
                    pantryAvailability={pantryAvailability}
                    requiredG={requiredG}
                    fpcLabels={fpcLabels}
                    onAdded={handleClose}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
